import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ImageOff } from "lucide-react";
import { Message } from "@/i18n/Message";
import type { DiscoveryCardModel } from "./types";

type Candidate = { node: HTMLElement; ratio: number; failed: boolean };
type Coordinator = { active: string | null; register: (id: string, node: HTMLElement) => () => void;
  hover: (id: string) => void; leave: (id: string) => void; fail: (id: string) => void };
const PreviewContext = createContext<Coordinator | null>(null);
export const previewIdentity = (entry: DiscoveryCardModel) => `${entry.selection.id}:${entry.publishedVersion ?? ""}`;

/** One viewport-wide owner: preview playback never launches an application. */
export function RecordedPreviewProvider({ children, enabled = true }: { children: ReactNode; enabled?: boolean }) {
  const [active, setActive] = useState<string | null>(null);
  const activeRef = useRef<string | null>(null);
  const candidates = useRef(new Map<string, Candidate>());
  const observer = useRef<IntersectionObserver | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const state = useRef({ enabled, mobile: false, reduced: false });
  state.current.enabled = enabled;
  const choose = useCallback((id: string | null) => {
    const candidate = id ? candidates.current.get(id) : null;
    if (id && (!state.current.enabled || state.current.reduced || document.hidden ||
      !candidate || candidate.failed || candidate.ratio < 0.65)) return;
    activeRef.current = id;
    setActive(id);
  }, []);
  const cancelTimer = useCallback(() => { window.clearTimeout(timer.current); }, []);
  const scheduleMobile = useCallback(() => {
    cancelTimer();
    if (!state.current.mobile || !state.current.enabled || state.current.reduced || document.hidden) return;
    timer.current = window.setTimeout(() => {
      let best: string | null = null;
      let distance = Infinity;
      for (const [id, candidate] of candidates.current) {
        if (candidate.ratio < 0.65 || candidate.failed) continue;
        const rect = candidate.node.getBoundingClientRect();
        const delta = Math.abs(rect.top + rect.height / 2 - window.innerHeight / 2);
        if (delta < distance) { best = id; distance = delta; }
      }
      choose(best);
    }, 250);
  }, [cancelTimer, choose]);
  const register = useCallback((id: string, node: HTMLElement) => {
    candidates.current.set(id, { node, ratio: 0, failed: false });
    observer.current?.observe(node);
    return () => {
      observer.current?.unobserve(node);
      candidates.current.delete(id);
      if (activeRef.current === id) choose(null);
    };
  }, [choose]);
  useEffect(() => {
    if (typeof window.matchMedia !== "function" || typeof IntersectionObserver !== "function") return;
    const mobile = window.matchMedia("(max-width: 899px), (hover: none), (pointer: coarse)");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const preferences = () => {
      state.current.mobile = mobile.matches;
      state.current.reduced = reduced.matches;
      cancelTimer();
      choose(null);
      scheduleMobile();
    };
    observer.current = new IntersectionObserver(entries => {
      for (const entry of entries) {
        for (const [id, candidate] of candidates.current) {
          if (candidate.node !== entry.target) continue;
          candidate.ratio = entry.intersectionRatio;
          if (activeRef.current === id && candidate.ratio < 0.65) choose(null);
        }
      }
      scheduleMobile();
    }, { threshold: [0, 0.65, 1] });
    for (const candidate of candidates.current.values()) observer.current.observe(candidate.node);
    const scroll = () => { if (state.current.mobile) { choose(null); scheduleMobile(); } };
    const visibility = () => { cancelTimer(); choose(null); if (!document.hidden) scheduleMobile(); };
    mobile.addEventListener("change", preferences);
    reduced.addEventListener("change", preferences);
    window.addEventListener("scroll", scroll, true);
    document.addEventListener("visibilitychange", visibility);
    preferences();
    return () => {
      cancelTimer(); observer.current?.disconnect(); observer.current = null;
      mobile.removeEventListener("change", preferences); reduced.removeEventListener("change", preferences);
      window.removeEventListener("scroll", scroll, true); document.removeEventListener("visibilitychange", visibility);
    };
  }, [choose, cancelTimer, scheduleMobile]);
  useEffect(() => { if (!enabled) { cancelTimer(); choose(null); } else scheduleMobile(); }, [enabled, cancelTimer, choose, scheduleMobile]);
  const hover = useCallback((id: string) => {
    if (state.current.mobile) return;
    cancelTimer();
    timer.current = window.setTimeout(() => choose(id), 350);
  }, [choose, cancelTimer]);
  const leave = useCallback((id: string) => {
    if (state.current.mobile) return;
    cancelTimer();
    if (activeRef.current === id) choose(null);
  }, [choose, cancelTimer]);
  const fail = useCallback((id: string) => {
    const candidate = candidates.current.get(id);
    if (candidate) candidate.failed = true;
    if (activeRef.current === id) choose(null);
    scheduleMobile();
  }, [choose, scheduleMobile]);
  const value = useMemo(() => ({ active, register, hover, leave, fail }), [active, register, hover, leave, fail]);
  return <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>;
}

export function useRecordedPreviewActions() { return useContext(PreviewContext); }

export default function RecordedAppPreview({ entry }: { entry: DiscoveryCardModel }) {
  const coordinator = useContext(PreviewContext);
  const media = useRef<HTMLSpanElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [failedStills, setFailedStills] = useState<readonly string[]>([]);
  const identity = previewIdentity(entry);
  const recording = entry.recordedPreview;
  const active = !!recording && coordinator?.active === identity;
  const register = coordinator?.register;
  useEffect(() => {
    if (recording && media.current && register) return register(identity, media.current.closest<HTMLElement>(".discovery-card") ?? media.current);
  }, [identity, recording?.url, register]);
  useEffect(() => {
    setPlaying(false);
    const element = video.current;
    if (!active || !element) return;
    let cancelled = false;
    element.muted = true;
    void element.play().catch(() => { if (!cancelled) coordinator?.fail(identity); });
    return () => { cancelled = true; element.pause(); element.removeAttribute("src"); element.load(); };
  }, [active, identity, recording?.url]);
  // The recording's poster leads; a missing poster falls back to the listing
  // screenshot before the card admits there is no preview.
  const still = [recording?.poster_url, entry.thumbnailUrl].find(
    (url): url is string => !!url && !failedStills.includes(url)) ?? null;
  return <span ref={media} className="discovery-card-media" data-recorded-preview={recording ? identity : undefined}>
    {still ? <img key={still} className="discovery-card-thumbnail" src={still} alt="" loading="lazy" onError={() => setFailedStills(list => [...list, still])} />
      : <span className="discovery-card-preview-empty"><ImageOff size={20} aria-hidden="true" /><Message message="Preview unavailable" /></span>}
    {active ? <video ref={video} className={`discovery-card-video${playing ? " discovery-card-video--playing" : ""}`}
      src={recording!.url} poster={still ?? undefined} muted loop playsInline preload="auto" aria-hidden="true"
      onPlaying={() => setPlaying(true)} onError={() => coordinator?.fail(identity)} /> : null}
  </span>;
}
